import { Flex, Tooltip } from '@mantine/core';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import type { StrategicEpic } from '../../../api/tasks-client/task-client.type';
import { ProgressBar } from '../../../components/progress-bar/progress-bar';
import type { TableColumn } from '../../../components/table/table';
import { safeParseDateInput } from '../../../helpers/date-time-helpers';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { primaryBase } from '../../../styles/design-tokens';
import { Text } from '../../../ui-library/typography/typography';
import { EntityIDCell } from '../components';
import { StrategyEntityType } from '../strategy.type';

/**
 * Build the epic-list table columns against the caller's translation
 * function so org-level text_config overrides apply.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {TableColumn<StrategicEpic>[]} Column definitions for `<Table>`
 */
const getEpicsTableColumns = (t: TFunction): TableColumn<StrategicEpic>[] => [
  {
    key: 'title',
    label: t('strategy.initiatives.table.epic_id'),
    width: '10%',
    sortable: true,
    render: (_, entity) => <EntityIDCell entity={entity} entityType={StrategyEntityType.Epic} />,
  },
  {
    key: 'name',
    label: t('strategy.initiatives.table.epic_name'),
    width: '25%',
    sortable: true,
    render: (_, entity) => <Text size="small">{entity.name}</Text>,
  },
  {
    key: 'percent_progress',
    label: t('strategy.initiatives.table.completion'),
    width: '12%',
    sortable: true,
    render: (_, entity) => {
      const tasksProgress = entity.percent_progress ?? 0;
      const tooltipLabel = `${t('common.work_units')}: ${tasksProgress.toFixed(1)}%`;

      return (
        <Tooltip label={tooltipLabel} withArrow>
          <Flex align="center" gap={8} style={{ width: '100%' }}>
            <ProgressBar percent={tasksProgress} color={primaryBase} />
            <Text size="small">{tasksProgress.toFixed(0)}%</Text>
          </Flex>
        </Tooltip>
      );
    },
  },
  {
    key: 'scope_increase_tasks_3m',
    label: t('strategy.epics.scope_change'),
    width: '12%',
    sortable: true,
    render: (_, entity) => {
      const tasksScopeChange = entity.scope_increase_tasks_3m ?? 0;
      const pointsScopeChange = entity.scope_increase_points_3m ?? 0;
      const tooltipLabel = `${t('common.work_units')}: ${tasksScopeChange >= 0 ? '+' : ''}${tasksScopeChange.toFixed(1)}% | ${t('common.estimation')}: ${pointsScopeChange >= 0 ? '+' : ''}${pointsScopeChange.toFixed(1)}%`;
      const displayValue = `${tasksScopeChange >= 0 ? '+' : ''}${tasksScopeChange.toFixed(1)}%`;

      return (
        <Tooltip label={tooltipLabel} withArrow>
          <Text size="small">{displayValue}</Text>
        </Tooltip>
      );
    },
  },
  {
    key: 'created_date',
    label: t('strategy.epics.created'),
    width: '12%',
    sortable: true,
    render: (_, entity) => {
      const createdDate = (entity as any).created_date;
      const formatted = createdDate ? new Date(createdDate).toLocaleDateString() : '-';
      return <Text size="small">{formatted}</Text>;
    },
  },
  {
    key: 'due_date',
    label: t('strategy.epics.due_date'),
    width: '12%',
    sortable: true,
    render: (_, entity) => {
      const dueDate = (entity as any).due_date;
      const formatted = dueDate ? new Date(dueDate).toLocaleDateString() : '-';
      return <Text size="small">{formatted}</Text>;
    },
  },
  {
    key: 'parent_project',
    label: t('strategy.epics.parent_project'),
    width: '15%',
    sortable: false,
    render: (_, entity) => {
      const parentProject = (entity as any).parent_project;
      const label = parentProject?.name || '-';
      return <Text size="small">{label}</Text>;
    },
  },
  {
    key: 'contributing_projects',
    label: t('strategy.epics.contributing_teams'),
    width: '20%',
    sortable: false,
    render: (_, entity) => {
      const projects = (entity as any).contributing_projects || [];
      const teamNames = projects.map((p: any) => p.name);
      const label = teamNames.length > 0 ? teamNames.join(', ') : '-';
      return <Text size="small">{label}</Text>;
    },
  },
];

/**
 * Custom hook that returns filtered epics based on search term, due date state, selected projects,
 * target end date filters, and created date filters
 *
 * @returns {StrategicEpic[]} Filtered array of epics
 */
function useFilteredEpics(): StrategicEpic[] {
  const epics = useStrategyStore((state) => state.epics as StrategicEpic[]);
  const searchTerm = useStrategyStore((state) => state.searchTerm);
  // Use stable selectors to avoid infinite loops with getSnapshot
  const epicDueDateStateFilters = useStrategyStore((state) => state.filters.epic_due_date_state);
  const epicProjectFilters = useStrategyStore((state) => state.filters.epic_projects);
  const epicCompletionFilters = useStrategyStore((state) => state.filters.epic_completion);
  const epicTargetEndDateFilters = useStrategyStore((state) => state.filters.epic_target_end_date);
  const epicCreatedDateFilters = useStrategyStore((state) => state.filters.epic_created_date);

  return useMemo(() => {
    let filtered = [...epics];

    // Apply search filter
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (epic) =>
          epic.title?.toLowerCase().includes(lowerSearchTerm) || epic.name?.toLowerCase().includes(lowerSearchTerm),
      );
    }

    // Apply completion filter
    const completionFilters = epicCompletionFilters || [];
    if (completionFilters.length > 0) {
      filtered = filtered.filter((epic) => {
        const progress = epic.percent_progress ?? 0;
        if (completionFilters.includes('not_started') && progress === 0) return true;
        if (completionFilters.includes('in_progress') && progress > 0 && progress < 100) return true;
        if (completionFilters.includes('completed') && progress === 100) return true;
        return false;
      });
    }

    // Apply target end date filter (due_date)
    const targetEndDateFilters = epicTargetEndDateFilters || [];
    if (targetEndDateFilters.length > 0) {
      const today = dayjs();
      const ninetyDaysAgo = dayjs().subtract(90, 'days');
      const ninetyDaysFromNow = dayjs().add(90, 'days');

      const showNext90Days = targetEndDateFilters.includes('next_90_days');
      const showLast90Days = targetEndDateFilters.includes('last_90_days');

      filtered = filtered.filter((epic) => {
        if (!epic.due_date) return false;
        const dueDate = safeParseDateInput(epic.due_date);
        if (!dueDate) return false;

        // Check if due date is in next 90 days
        const isInNext90Days = dueDate.isAfter(today) && dueDate.isBefore(ninetyDaysFromNow);
        // Check if due date is in last 90 days
        const isInLast90Days = dueDate.isAfter(ninetyDaysAgo) && (dueDate.isBefore(today) || dueDate.isSame(today));

        if (showNext90Days && showLast90Days) {
          return isInNext90Days || isInLast90Days;
        } else if (showNext90Days) {
          return isInNext90Days;
        } else if (showLast90Days) {
          return isInLast90Days;
        }
        return true;
      });
    }

    // Apply created date filter
    const createdDateFilters = epicCreatedDateFilters || [];
    if (createdDateFilters.length > 0) {
      const ninetyDaysAgo = dayjs().subtract(90, 'days');

      const showLast90Days = createdDateFilters.includes('last_90_days');

      if (showLast90Days) {
        filtered = filtered.filter((epic) => {
          if (!epic.created_date) return false;
          const createdDate = safeParseDateInput(epic.created_date);
          if (!createdDate) return false;
          return createdDate.isAfter(ninetyDaysAgo) || createdDate.isSame(ninetyDaysAgo);
        });
      }
    }

    // Apply due date state filter
    // Only filter if specific states are selected and 'all' is not included
    const dueDateFilters = epicDueDateStateFilters || [];
    const hasSelectedStates = dueDateFilters.length > 0 && !dueDateFilters.includes('all');
    const showHasDueDate = dueDateFilters.includes('has_due_date');
    const showNoDueDate = dueDateFilters.includes('no_due_date');

    if (hasSelectedStates) {
      // If both are selected, show all; otherwise filter by the selected state(s)
      if (!showHasDueDate || !showNoDueDate) {
        // One or the other is selected, not both
        filtered = filtered.filter((epic) => {
          if (showHasDueDate) {
            return epic.due_date !== null;
          }
          if (showNoDueDate) {
            return epic.due_date === null;
          }
          return true;
        });
      }
      // If both are selected, don't filter (show all)
    }

    // Apply project filter (filter by parent project)
    const projectFilters = epicProjectFilters || [];
    if (projectFilters.length > 0) {
      filtered = filtered?.filter((epic) => epic?.parent_project && projectFilters.includes(epic.parent_project.id));
    }

    return filtered ?? [];
  }, [
    epics,
    searchTerm,
    epicDueDateStateFilters,
    epicProjectFilters,
    epicTargetEndDateFilters,
    epicCreatedDateFilters,
    epicCompletionFilters,
  ]);
}

/**
 * Filters epics based on search term
 *
 * @param {StrategicEpic[]} epics - Array of epics to filter
 * @param {string} searchTerm - The search term to filter by
 * @returns {StrategicEpic[]} Filtered array of epics
 */
function filterEpicsBySearch(epics: StrategicEpic[], searchTerm: string): StrategicEpic[] {
  if (!searchTerm.trim()) {
    return epics;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();

  return epics.filter(
    (epic) => epic.title?.toLowerCase().includes(lowerSearchTerm) || epic.name?.toLowerCase().includes(lowerSearchTerm),
  );
}

/**
 * Sorts epics by column
 *
 * @param {StrategicEpic[]} epics - Array of epics to sort
 * @param {keyof StrategicEpic} sortBy - The column to sort by
 * @param {'asc' | 'desc'} direction - The sort direction
 * @returns {StrategicEpic[]} Sorted array of epics
 */
function sortEpics(epics: StrategicEpic[], sortBy: keyof StrategicEpic, direction: 'asc' | 'desc'): StrategicEpic[] {
  const sorted = [...epics].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  return sorted;
}

/**
 * Exports epics to CSV
 *
 * @param {StrategicEpic[]} epics - Array of epics to export
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {void}
 */
function handleDownloadCSV(epics: StrategicEpic[], t: TFunction): void {
  const headers = [
    `${t('common.epic')} ID`,
    'Name',
    'Created Date',
    'Progress',
    'Due Date',
    t('strategy.epics.parent_project'),
    t('strategy.epics.contributing_teams'),
  ];

  const rows = (epics || []).map((epic) => [
    epic.title,
    epic.name,
    new Date(epic.created_date).toLocaleDateString(),
    isNaN(epic.percent_progress) ? '' : `${epic.percent_progress.toFixed(1)}%`,
    epic.due_date ? new Date(epic.due_date).toLocaleDateString() : '',
    epic.parent_project?.name || '',
    epic.contributing_projects?.map((p) => p.name).join('; '),
  ]);

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `epics-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);

  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export { filterEpicsBySearch, getEpicsTableColumns, handleDownloadCSV, sortEpics, useFilteredEpics };
